import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import MarketingHeader from '@/components/marketing/MarketingHeader';
import MarketingFooter from '@/components/marketing/MarketingFooter';
import {
  Calendar,
  Sparkles,
  Wrench,
  Hammer,
  Receipt,
  BarChart3,
  Mail,
  ShieldCheck,
  RefreshCw,
  ArrowRight,
  Check,
  Users,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { joinWaitlist } from '@/lib/api';
import { trackEvent } from '@/lib/metaPixel';
import { toast } from 'sonner';

const FEATURES = [
  {
    icon: Calendar,
    title: 'Unified Calendar',
    body: 'Bookings, cleanings, maintenance, landscaping, renovations, and owner-use blocks — drag-to-reschedule, click to edit.',
  },
  {
    icon: RefreshCw,
    title: 'iCal Sync Both Ways',
    body: 'Pull in Airbnb, VRBO, Booking.com, and Evolve. Push your blocked dates back. No more double-bookings.',
  },
  {
    icon: Sparkles,
    title: 'Turnover Cleanings',
    body: 'Auto-scheduled after every checkout. Photo uploads, work notes, and hours-tracked invoicing for your crew.',
  },
  {
    icon: Wrench,
    title: 'Maintenance & Recurring Tasks',
    body: 'HVAC every 6 months, gutters every spring, smoke detectors every quarter. Set once, never forget.',
  },
  {
    icon: Hammer,
    title: 'Renovation Tracker',
    body: 'Budget vs. actuals, contractor info, progress %, photo galleries. Keep multi-month projects from sliding.',
  },
  {
    icon: Receipt,
    title: 'Expenses & Invoices',
    body: 'Snap receipts, tag categories, generate invoices from tracked hours. Tax time stops being a nightmare.',
  },
  {
    icon: BarChart3,
    title: 'Revenue Dashboard',
    body: 'Occupancy, ADR, RevPAR, profit margin — by property, by month, by channel. The board you actually open.',
  },
  {
    icon: Users,
    title: 'Staff Seats',
    body: 'Invite cleaners, handymen, and landscapers. They see only what they need. You stay in control.',
  },
];

const TESTIMONIALS = [
  {
    quote:
      "Cancelled three tools the week we switched. Calendar drag-and-drop alone paid for the year.",
    name: 'Marisol Vega',
    role: '12 doors · Tampa',
  },
  {
    quote:
      "First quarter where the cleanings, repairs, and books actually agreed with each other.",
    name: 'Derek Aigbe',
    role: '4 doors · Asheville',
  },
  {
    quote:
      "The renovation tracker saved me from a $14k overrun. Worth it just for that.",
    name: 'Carla Pham',
    role: '21 doors · Big Bear',
  },
];

// Subtle gold rule used as heraldic divider accent
const GoldRule = () => (
  <div className="flex items-center gap-3" aria-hidden="true">
    <span className="h-px w-10 bg-gradient-to-r from-transparent to-amber-300/60" />
    <span className="w-1.5 h-1.5 rotate-45 bg-amber-300/80" />
    <span className="h-px w-10 bg-gradient-to-l from-transparent to-amber-300/60" />
  </div>
);

export default function LandingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistSubmitted, setWaitlistSubmitted] = useState(false);

  useEffect(() => {
    if (user) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    if (!waitlistEmail.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    try {
      await joinWaitlist({ email: waitlistEmail, source: 'landing_form' });
      trackEvent('Lead', { content_name: 'Waitlist - Landing' });
      setWaitlistSubmitted(true);
      toast.success("You're on the list. We'll email when billing goes live.");
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not join waitlist');
    }
  };

  return (
    <div className="min-h-screen bg-[#070d1c] text-slate-100">
      <MarketingHeader />

      {/* HERO */}
      <section className="relative overflow-hidden pt-32 pb-24 lg:pt-44 lg:pb-32">
        {/* Background ornaments — gold + royal-blue glow on deep navy */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-32 -left-32 h-[420px] w-[420px] rounded-full bg-amber-400/10 blur-3xl" />
          <div className="absolute top-1/3 -right-40 h-[520px] w-[520px] rounded-full bg-sky-500/15 blur-3xl" />
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 h-[300px] w-[800px] rounded-full bg-blue-600/10 blur-3xl" />
          <div
            className="absolute inset-0 opacity-[0.06]"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, rgba(202,170,98,0.7) 1px, transparent 0)",
              backgroundSize: '28px 28px',
            }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-amber-300/30 bg-amber-300/10 text-xs text-amber-200 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-300 animate-pulse" />
              New: Renovations + Owner-Use blocks
            </div>

            <h1 className="font-manrope font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-white">
              Run every property like
              <br />
              <span className="bg-gradient-to-r from-amber-200 via-amber-100 to-sky-200 bg-clip-text text-transparent">
                it&apos;s your only one.
              </span>
            </h1>

            <p className="mt-6 text-lg text-slate-300 max-w-xl leading-relaxed">
              <span className="text-white font-semibold">PropVeiw</span>{' '}
              <span className="text-amber-200">
                (V.E.I.W. — Virtual Engagement, Intelligent Workflows)
              </span>{' '}
              keeps property ops in one place — maintenance, cleaning,
              rentals, expenses, renovations &amp; new builds. Track photos,
              teams &amp; emergencies in real time so nothing gets missed —
              always in control.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/register">
                <Button
                  size="lg"
                  className="bg-amber-400 hover:bg-amber-300 text-[#070d1c] font-semibold h-12 px-6 shadow-lg shadow-amber-500/20"
                  data-testid="hero-cta-trial"
                >
                  Start your 14-day trial
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-[#2a4a7a] bg-[#0c1530]/60 text-slate-200 hover:bg-[#13234a] hover:text-white h-12 px-6"
                  data-testid="hero-cta-pricing"
                >
                  See pricing
                </Button>
              </Link>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-400">
              <span className="flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5 text-amber-300" />
                Data isolated per organization
              </span>
              <span className="flex items-center gap-1.5">
                <Check className="h-3.5 w-3.5 text-amber-300" />
                No credit card to demo
              </span>
              <span className="flex items-center gap-1.5">
                <Check className="h-3.5 w-3.5 text-amber-300" />
                Cancel anytime
              </span>
            </div>
          </div>

          {/* Product card mock */}
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-amber-400/15 via-sky-500/10 to-blue-600/10 rounded-2xl blur-2xl" />
            <div className="relative bg-[#0c1530]/80 backdrop-blur border border-[#1f3a64] rounded-2xl shadow-2xl overflow-hidden">
              <div className="flex items-center gap-1.5 px-4 py-3 border-b border-[#1f3a64]">
                <span className="w-2.5 h-2.5 rounded-full bg-rose-400/70" />
                <span className="w-2.5 h-2.5 rounded-full bg-amber-400/70" />
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/70" />
                <span className="ml-3 text-xs text-slate-500">propveiw.com/dashboard</span>
              </div>
              <div className="p-5 space-y-4">
                <div className="flex items-baseline justify-between">
                  <h3 className="font-manrope font-semibold text-white">June 2026 · 4 doors</h3>
                  <span className="text-xs text-slate-500">Active bookings 7 · Tasks 12</span>
                </div>
                <div className="grid grid-cols-7 gap-1.5">
                  {Array.from({ length: 21 }).map((_, i) => {
                    const cellKey = `hero-cell-${i}`;
                    const has = i % 3 === 0 || i === 5 || i === 11;
                    const kind = i % 5 === 0 ? 'b' : i % 4 === 0 ? 'c' : i === 11 ? 'l' : i === 14 ? 'r' : 'm';
                    const color =
                      kind === 'b' ? 'bg-sky-400/80'
                      : kind === 'c' ? 'bg-amber-300/80'
                      : kind === 'l' ? 'bg-emerald-400/80'
                      : kind === 'r' ? 'bg-violet-400/80'
                      : 'bg-blue-400/80';
                    return (
                      <div
                        key={cellKey}
                        className={`h-12 rounded-md border ${has ? 'border-[#2a4a7a]' : 'border-[#1f3a64]/50'} bg-[#13234a]/40 p-1 flex items-end`}
                      >
                        {has && <div className={`h-1.5 w-full rounded-sm ${color}`} />}
                      </div>
                    );
                  })}
                </div>

                <div className="grid grid-cols-3 gap-3 pt-2">
                  <div className="rounded-lg bg-[#13234a]/60 border border-[#2a4a7a]/60 p-3">
                    <div className="text-xs text-slate-400">Revenue MTD</div>
                    <div className="text-lg font-semibold text-white mt-1">$18.4k</div>
                    <div className="text-xs text-amber-300">+12.3%</div>
                  </div>
                  <div className="rounded-lg bg-[#13234a]/60 border border-[#2a4a7a]/60 p-3">
                    <div className="text-xs text-slate-400">Occupancy</div>
                    <div className="text-lg font-semibold text-white mt-1">87%</div>
                    <div className="text-xs text-sky-300">+4.1pt</div>
                  </div>
                  <div className="rounded-lg bg-[#13234a]/60 border border-[#2a4a7a]/60 p-3">
                    <div className="text-xs text-slate-400">Profit</div>
                    <div className="text-lg font-semibold text-white mt-1">$11.2k</div>
                    <div className="text-xs text-amber-300">61% margin</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LOGOS / STAT BAND */}
      <section className="border-y border-[#1f3a64]/80 bg-[#0c1530]/40 relative">
        {/* heraldic gold hairline */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-300/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-300/40 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { v: '4,200+', l: 'nights managed / day' },
            { v: '99.95%', l: 'sync uptime' },
            { v: '14-day', l: 'free trial' },
            { v: '< 60s', l: 'to import a property' },
          ].map((s) => (
            <div key={s.l}>
              <div className="font-manrope text-2xl font-semibold text-white">{s.v}</div>
              <div className="text-xs text-slate-500 mt-1 uppercase tracking-wider">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* V.E.I.W. ACRONYM STRIP */}
      <section className="relative py-20 bg-[#070d1c] overflow-hidden">
        <div className="absolute -top-24 left-1/2 -translate-x-1/2 h-[260px] w-[820px] rounded-full bg-amber-400/[0.06] blur-3xl pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-amber-300 text-sm font-medium uppercase tracking-widest">
              What V.E.I.W. stands for
            </p>
            <h2 className="font-manrope text-3xl sm:text-4xl font-bold text-white mt-3 tracking-tight">
              A meticulous name for a meticulous product.
            </h2>
            <div className="mt-5 flex justify-center"><GoldRule /></div>
            <p className="mt-5 text-slate-400">
              Every letter earns its place — the way every doc, task, and
              dollar in PropVeiw has a home.
            </p>
          </div>

          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                letter: 'V',
                word: 'Virtual',
                body: 'Work from anywhere — desktop, tablet, or a phone in a mechanical room. Your whole portfolio is one login away.',
              },
              {
                letter: 'E',
                word: 'Engagement',
                body: 'Owners, staff, cleaners, and contractors stay in sync with photos, notes, and real-time task updates.',
              },
              {
                letter: 'I',
                word: 'Intelligent',
                body: 'Recurring maintenance schedules, occupancy analytics, and profit dashboards do the thinking so you can act.',
              },
              {
                letter: 'W',
                word: 'Workflows',
                body: 'Bookings, cleanings, repairs, renovations, expenses — each with its own opinionated flow, not a generic checklist.',
              },
            ].map(({ letter, word, body }) => (
              <div
                key={letter}
                className="relative group rounded-xl border border-[#1f3a64] bg-[#0c1530]/60 p-6 hover:border-amber-300/40 hover:bg-[#0c1530] transition-colors"
              >
                <div className="flex items-baseline gap-3">
                  <span className="font-manrope font-black text-5xl bg-gradient-to-b from-amber-200 to-amber-400 bg-clip-text text-transparent leading-none">
                    {letter}
                  </span>
                  <span className="font-manrope font-semibold text-white text-lg">
                    {word}
                  </span>
                </div>
                <p className="mt-4 text-sm text-slate-400 leading-relaxed">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="py-24 bg-[#070d1c]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-amber-300 text-sm font-medium uppercase tracking-widest">Everything in one place</p>
            <h2 className="font-manrope text-3xl sm:text-4xl font-bold text-white mt-3 tracking-tight">
              Eight workflows. One opinionated app.
            </h2>
            <p className="text-slate-400 mt-4">
              PROP VEIW replaces the spreadsheet, the cleaning text thread, the contractor folder, and that
              one shared Google Calendar nobody trusts. Each module is built around real ops, not generic
              project-management primitives.
            </p>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {FEATURES.map((f, i) => {
              const iconTheme = i % 2 === 0
                ? 'bg-amber-400/10 text-amber-300 ring-1 ring-amber-400/20'
                : 'bg-sky-500/10 text-sky-300 ring-1 ring-sky-500/20';
              return (
                <div
                  key={f.title}
                  className="relative group rounded-xl border border-[#1f3a64] bg-[#0c1530]/60 p-5 hover:border-amber-300/40 hover:bg-[#0c1530] transition-colors"
                >
                  <div className="absolute top-5 right-5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <ArrowRight className="h-4 w-4 text-amber-300" />
                  </div>
                  <div className={`w-9 h-9 rounded-lg ${iconTheme} flex items-center justify-center mb-4`}>
                    <f.icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-manrope font-semibold text-white">{f.title}</h3>
                  <p className="text-sm text-slate-400 mt-2 leading-relaxed">{f.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* MULTI-TENANT / SECURITY STRIP */}
      <section className="py-20 bg-gradient-to-br from-[#0c1530] via-[#0c1530] to-[#070d1c] border-y border-[#1f3a64] relative overflow-hidden">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 h-[300px] w-[700px] rounded-full bg-sky-500/10 blur-3xl pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-amber-400/10 text-amber-300 ring-1 ring-amber-400/30 mb-6">
            <ShieldCheck className="h-6 w-6" />
          </div>
          <h2 className="font-manrope text-3xl sm:text-4xl font-bold text-white tracking-tight">
            Your data never touches another manager&apos;s.
          </h2>
          <div className="mt-5 flex justify-center"><GoldRule /></div>
          <p className="mt-5 text-slate-300 max-w-2xl mx-auto">
            Every property, booking, expense, and task is scoped to your organization. Staff you invite
            inherit only your data. We treat data isolation as a security feature, not a feature-flag.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-slate-400">
            <span className="flex items-center gap-2"><Check className="h-4 w-4 text-amber-300" /> JWT-secured sessions</span>
            <span className="flex items-center gap-2"><Check className="h-4 w-4 text-amber-300" /> Per-org data isolation</span>
            <span className="flex items-center gap-2"><Check className="h-4 w-4 text-amber-300" /> Role-based access</span>
            <span className="flex items-center gap-2"><Check className="h-4 w-4 text-amber-300" /> Encrypted at rest</span>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 bg-[#070d1c]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <p className="text-amber-300 text-sm font-medium uppercase tracking-widest">Operator love</p>
            <h2 className="font-manrope text-3xl sm:text-4xl font-bold text-white mt-3 tracking-tight">
              Managers who got their Sundays back.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {TESTIMONIALS.map((t) => (
              <figure
                key={t.name}
                className="rounded-xl border border-[#1f3a64] bg-[#0c1530]/60 p-6 hover:border-[#2a4a7a] transition-colors"
              >
                <blockquote className="text-slate-200 leading-relaxed">&ldquo;{t.quote}&rdquo;</blockquote>
                <figcaption className="mt-4 pt-4 border-t border-[#1f3a64] flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-300/50 to-sky-400/40 flex items-center justify-center text-sm font-semibold text-[#070d1c]">
                    {t.name.charAt(0)}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-white">{t.name}</div>
                    <div className="text-xs text-slate-400">{t.role}</div>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* WAITLIST + CTA */}
      <section className="py-24 bg-gradient-to-br from-[#0c1530] to-[#070d1c] relative overflow-hidden">
        <div className="absolute -top-20 -left-20 h-[300px] w-[300px] rounded-full bg-amber-400/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-20 -right-20 h-[300px] w-[300px] rounded-full bg-sky-500/10 blur-3xl pointer-events-none" />
        <div className="relative max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-amber-300/30 bg-amber-300/10 text-xs text-amber-200 mb-6">
            <Mail className="h-3 w-3" />
            Paid plans launching soon
          </div>
          <h2 className="font-manrope text-3xl sm:text-4xl font-bold text-white tracking-tight">
            Be first when billing opens.
          </h2>
          <div className="mt-5 flex justify-center"><GoldRule /></div>
          <p className="mt-5 text-slate-300">
            Drop your email and we&apos;ll send you a one-click trial activation and a founder-tier
            discount. Free to use today — we just want a heads-up so we can prep your account.
          </p>

          {!waitlistSubmitted ? (
            <form
              onSubmit={handleWaitlistSubmit}
              className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
              data-testid="waitlist-form"
            >
              <Input
                type="email"
                value={waitlistEmail}
                onChange={(e) => setWaitlistEmail(e.target.value)}
                placeholder="you@yourcompany.com"
                className="bg-[#0c1530]/70 border-[#2a4a7a] text-white placeholder:text-slate-500 h-12"
                data-testid="waitlist-email-input"
                required
              />
              <Button
                type="submit"
                className="bg-amber-400 hover:bg-amber-300 text-[#070d1c] font-semibold h-12 px-6 shadow-lg shadow-amber-500/20"
                data-testid="waitlist-submit-btn"
              >
                Get on the list
              </Button>
            </form>
          ) : (
            <div
              className="mt-8 max-w-md mx-auto rounded-lg border border-amber-300/30 bg-amber-300/10 p-4 text-amber-100 text-sm"
              data-testid="waitlist-success"
            >
              <Check className="inline h-4 w-4 mr-1" />
              You&apos;re on the list. Check your inbox when we launch.
            </div>
          )}
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
